import { useEffect, useRef } from "react";
import Editor from "@toast-ui/editor";
import "@toast-ui/editor/dist/toastui-editor.css";

import { ApiError, apiUploadFormData } from "@/lib/api";

function editorMarkdown(steps: string): string {
  const trimmed = steps.replace(/\r\n/g, "\n").trim();
  if (trimmed === "") return "1. ";
  if (/^\s*(?:\d+\.|[-*+])\s/m.test(trimmed) || trimmed.includes("![") || trimmed.includes("**")) {
    return trimmed;
  }
  return trimmed
    .split("\n")
    .filter((line) => line.trim() !== "")
    .map((line, index) => `${index + 1}. ${line.trim()}`)
    .join("\n");
}

function stepsToStore(markdown: string): string {
  const text = markdown.replace(/\r\n/g, "\n").trim();
  const withoutMarkers = text.replace(/^\s*\d+\.\s?/gm, "").replace(/^\s*[-*+]\s?/gm, "").trim();
  return withoutMarkers === "" ? "" : text;
}

/**
 * Toast UI editor. The Markdown / WYSIWYG switch is part of the editor.
 * An empty document starts as an ordered list. Pictures upload into the script.
 */
export function OperatorScriptStepsEditor({
  value,
  disabled,
  scriptId,
  onCommit,
  onUploadError,
}: {
  value: string;
  disabled: boolean;
  scriptId: string | undefined;
  onCommit: (steps: string) => void;
  onUploadError: (message: string) => void;
}): JSX.Element {
  const hostRef = useRef<HTMLDivElement>(null);
  const editorRef = useRef<Editor | null>(null);
  const scriptIdRef = useRef(scriptId);
  const onCommitRef = useRef(onCommit);
  const onUploadErrorRef = useRef(onUploadError);
  scriptIdRef.current = scriptId;
  onCommitRef.current = onCommit;
  onUploadErrorRef.current = onUploadError;

  useEffect(() => {
    const host = hostRef.current;
    if (host == null) return;
    const editor = new Editor({
      el: host,
      initialEditType: "wysiwyg",
      hideModeSwitch: false,
      usageStatistics: false,
      height: "280px",
      initialValue: editorMarkdown(value),
      toolbarItems: [["heading", "bold", "italic"], ["ol", "ul"], ["image"]],
      hooks: {
        addImageBlobHook(blob, callback) {
          const id = scriptIdRef.current;
          if (id == null) {
            onUploadErrorRef.current("Save the script name before adding a picture.");
            return;
          }
          const formData = new FormData();
          const file = blob instanceof File ? blob : new File([blob], "picture.png", { type: blob.type || "image/png" });
          formData.append("file", file);
          void apiUploadFormData<{ url: string }>(`/api/quality/operator-scripts/${id}/images`, formData)
            .then((uploaded) => {
              callback(uploaded.url, "picture");
              onCommitRef.current(stepsToStore(editor.getMarkdown()));
            })
            .catch((error: unknown) => {
              onUploadErrorRef.current(error instanceof ApiError ? error.message : "Could not add the picture.");
            });
        },
      },
      events: {
        blur() {
          onCommitRef.current(stepsToStore(editor.getMarkdown()));
        },
      },
    });
    editorRef.current = editor;
    return () => {
      editor.destroy();
      editorRef.current = null;
    };
  }, []);

  useEffect(() => {
    const editor = editorRef.current;
    if (editor == null) return;
    const next = editorMarkdown(value);
    if (editor.getMarkdown().trim() === next.trim()) return;
    editor.setMarkdown(next);
  }, [value]);

  return <div ref={hostRef} className={disabled ? "pointer-events-none opacity-70" : undefined} />;
}
